import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Mail, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useApproveUser, useRejectUser } from '@/features/users/hooks';
import { triggerHapticFeedback, ADMIN_COLORS } from '../utils/adminAnimations';
import type { PendingUser } from '@/features/users/types';

interface PendingUserApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: PendingUser;
}

export function PendingUserApprovalModal({
  isOpen,
  onClose,
  user,
}: PendingUserApprovalModalProps) {
  const { t, i18n } = useTranslation();
  const { openConfirm } = useModal();
  const approve = useApproveUser();
  const reject = useRejectUser();

  const handleApprove = useCallback(async () => {
    triggerHapticFeedback();
    try {
      await approve.mutateAsync({
        id: user.id,
      });
      toast.success(t('userApprovals.approved'));
      onClose();
    } catch {
      toast.error(t('common.error'));
    }
  }, [user.id, approve, onClose, t]);

  const handleReject = () => {
    triggerHapticFeedback();
    openConfirm({
      title: t('userApprovals.rejectConfirmTitle'),
      message: t('userApprovals.rejectConfirmMessage'),
      variant: 'danger',
      confirmLabel: t('userApprovals.reject'),
      onConfirm: async () => {
        await reject.mutateAsync(user.id);
        toast.success(t('userApprovals.rejected'));
        onClose();
      },
    });
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('userApprovals.title')}
      isPending={approve.isPending}
      onSubmit={handleApprove}
      isFormValid={true}
      submitLabel={t('userApprovals.approve')}
    >
      <div className="space-y-5">
        {/* User info card */}
        <div
          className="rounded-xl p-4 space-y-2"
          style={{
            backgroundColor: MODAL_COLORS.interactive,
            border: `1px solid ${MODAL_COLORS.border}`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg"
              style={{
                backgroundColor: ADMIN_COLORS.warningBg,
                color: ADMIN_COLORS.warningText,
                border: `1px solid ${ADMIN_COLORS.border}`,
              }}
            >
              {(user.firstName?.[0] ?? user.email[0]).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-base" style={{ color: MODAL_COLORS.text }}>
                {user.firstName} {user.lastName}
              </p>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Mail className="w-3 h-3" />
                <span className="truncate">{user.email}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5">
                <Calendar className="w-3 h-3" />
                <span>
                  {t('userApprovals.registeredOn')}{' '}
                  {new Date(user.createdAt).toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Reject button */}
        <button
          type="button"
          onClick={handleReject}
          disabled={reject.isPending}
          className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
          style={{
            backgroundColor: '#FEE2E2',
            color: '#B91C1C',
            border: `1px solid ${MODAL_COLORS.border}`,
          }}
        >
          {reject.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <X className="w-4 h-4" />
          )}
          {t('userApprovals.reject')}
        </button>
      </div>
    </FormModal>
  );
}
